<script setup lang="ts">
import type { CandidatesPageHeader } from "@/types/marketing";

interface Props {
  content: CandidatesPageHeader;
}

defineProps<Props>();

const { candidateRegisterUrl } = useCandidateUrls();

const toCandidateRegister = () => {
  navigateTo(candidateRegisterUrl, { external: true, open: { target: "_blank" } });
};
</script>

<template>
  <MarketingHeroSection>
    <!-- Centered Content -->
    <div class="text-center max-w-4xl 2xl:max-w-5xl 3xl:max-w-6xl mx-auto">
      <!-- Badge -->
      <div class="hero-fade-up mb-6 2xl:mb-8">
        <div class="badge-hospitality inline-flex 2xl:text-base 3xl:text-lg 4xl:text-xl">
          <span class="badge-hospitality-text">
            {{ content.badge }}
          </span>
        </div>
      </div>

      <!-- Headline -->
      <div class="space-y-4 2xl:space-y-6 hero-fade-up mb-6 2xl:mb-8" style="--animation-delay: 100ms">
        <h1
          class="font-secondary font-bold tracking-tight text-dark-gray text-4xl sm:text-5xl lg:text-6xl xl:text-7xl 2xl:text-8xl 3xl:text-9xl 4xl:text-[10rem] leading-[1.1]"
        >
          {{ content.title }}
        </h1>
        <p class="text-xl sm:text-2xl 2xl:text-3xl 3xl:text-4xl 4xl:text-5xl font-medium text-gradient-gold">
          {{ content.subtitle }}
        </p>
      </div>

      <!-- Description -->
      <p
        class="text-base sm:text-lg 2xl:text-xl 3xl:text-2xl 4xl:text-3xl leading-relaxed text-medium-gray max-w-2xl 2xl:max-w-3xl mx-auto hero-fade-up mb-8 2xl:mb-10"
        style="--animation-delay: 200ms"
      >
        {{ content.description }}
      </p>

      <!-- Primary CTAs -->
      <div class="hero-fade-up" style="--animation-delay: 300ms">
        <div class="flex flex-col sm:flex-row gap-3 sm:gap-4 2xl:gap-5 3xl:gap-6 justify-center mb-6 2xl:mb-8">
          <PrimaryButton
            @click="toCandidateRegister"
            large
            class="group rounded-full h-btn-sunset shadow-sunset hover:shadow-lg transition-all duration-300 motion-safe:hover:scale-105"
          >
            <span class="flex items-center justify-center text-base font-semibold normal-case tracking-normal">
              {{ content.cta.primary.text }}
              <Icon :name="content.cta.primary.icon" class="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </span>
          </PrimaryButton>

          <NuxtLink
            :to="ROUTES.browseJobs"
            class="group min-h-[3.15rem] max-h-[3.15rem] px-4 py-2 text-base rounded-full border-2 border-primary/20 bg-white/50 backdrop-blur-sm hover:border-primary/40 hover:bg-white transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Icon :name="content.cta.secondary.icon" class="w-5 h-5 text-primary shrink-0" />
            <span class="font-semibold text-dark-gray">{{ content.cta.secondary.text }}</span>
          </NuxtLink>
        </div>

        <!-- Benefits -->
        <div
          class="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 2xl:gap-x-8 2xl:gap-y-3 text-sm 2xl:text-base 3xl:text-lg 4xl:text-xl"
        >
          <div
            v-for="(benefit, index) in content.benefits"
            :key="`benefit-${index}`"
            class="flex items-center gap-1.5 2xl:gap-2"
            :class="index === 0 ? 'text-savanna' : 'text-medium-gray'"
          >
            <Icon :name="benefit.icon" class="w-4 h-4 2xl:w-5 2xl:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7" />
            <span :class="index === 0 ? 'font-medium' : ''">{{ benefit.text }}</span>
          </div>
        </div>
      </div>
    </div>
  </MarketingHeroSection>
</template>
